/**
 * MeshManager — Mesh & Discovery function group, MVP slice.
 *
 * Scope (function map MVP):
 *   - bootstrap()              — fire initial connections from peer_book
 *   - generateInviteCode()     — peer-level invite (NOT thread invite)
 *   - redeemInviteCode(code)   — process an incoming peer invite
 *   - addPeerMultiaddr(addr)   — manually add a peer to the book
 *   - listPeers()              — read peer_book
 *   - getMeshStats()           — combine MeshNode runtime stats with peer_book
 *   - markPeerSeen(peerId)     — update lastSeenAt (internal-but-useful)
 *
 * Not in scope (beta or later):
 *   - setTransportForSensitivity, setRendezvous, enableTransport — these
 *     touch transport_prefs which is not yet wired into the runtime
 *
 * The "invite" here is a PEER-LEVEL invite — it gives a stranger enough
 * information to dial us on the network (peerId + multiaddrs + optional
 * verification pubkey). It is *separate* from the thread-level invite
 * (`ThreadManager.inviteToThread`) which is an MLS welcome for a specific
 * group. The two concepts deliberately have different mechanisms:
 *
 *   - Peer invites: small, short-lived, plain-text encoded (no MLS).
 *     Whoever holds the code can dial me. Single- or multi-use.
 *   - Thread invites: MLS welcome bundles, addressed to a specific
 *     KeyPackage. Cryptographically scoped to one recipient.
 *
 * A typical onboarding flow combines them: Alice shares a peer invite
 * with Bob (Bob can now reach Alice on the network). Then, once they're
 * connected, Alice sends Bob a thread invite for whatever specific group.
 *
 * Per-device, never-synced. Peer discovery is by definition per-node.
 */

import { blake3 } from '@noble/hashes/blake3';
import { base32 } from '@scure/base';

import type { AppContext } from '../app';
import type { Context } from '../identity';
import type {
  AuditLogRecord,
  InviteLedgerRecord,
  PeerBookRecord,
  PeerSource,
  PeerTrustLevel,
} from '../storage/schema';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export interface BootstrapOptions {
  /** Maximum peers to dial in this run. Defaults to 8. */
  maxDials?: number;
  /** Skip peers with trustLevel='blocked'. Defaults true. */
  excludeBlocked?: boolean;
}

export interface BootstrapResult {
  attempted: number;
  succeeded: number;
  failed: number;
  /** Errors per peerId for failed dials, for diagnostics. */
  failures: Array<{ peerId: string; error: string }>;
}

export interface GenerateInviteOptions {
  /** How many redemptions to allow. Default 1 (single-use). */
  maxUses?: number;
  /** Expiry — ISO timestamp. Null = never expires. Default 24h from now. */
  expiresAt?: string | null;
  /** Optional label for tracking who this was issued for. */
  issuedFor?: string;
  /** Owning context (hierarchical mode). Defaults to a stable mesh context. */
  context?: Context;
}

export interface GenerateInviteResult {
  inviteId: string;
  /** Encoded invite code, base32. Suitable for QR. Prefix `mesh:v1:`. */
  code: string;
  /** ISO expiry (or null). */
  expiresAt: string | null;
}

export interface RedeemInviteOptions {
  /** The invite code string (`mesh:v1:<base32>`). */
  code: string;
}

export interface RedeemInviteResult {
  /** PeerID that issued the invite. */
  issuerPeerId: string;
  /** Multiaddrs we now know for that peer. */
  multiaddrs: string[];
  /** Whether the invite carried a signature we could verify. */
  signatureVerified: boolean;
}

export interface AddPeerMultiaddrOptions {
  peerId: string;
  multiaddr: string;
  alias?: string;
  source?: PeerSource;
  trustLevel?: PeerTrustLevel;
}

export interface ListPeersOptions {
  /** Filter to a specific trust level. Default: all. */
  trustLevel?: PeerTrustLevel;
  /** Include peers we've never connected to (lastSeenAt unset). Default true. */
  includeUnseen?: boolean;
}

export interface MeshStats {
  /** Our own peerId. */
  peerId: string;
  /** Currently-connected peer count (from MeshNode). */
  connected: number;
  /** Total known peers in our peer_book. */
  known: number;
  /** Counts by trust level. */
  byTrust: Record<PeerTrustLevel, number>;
  /** Uptime in seconds. */
  uptimeSeconds: number;
  /** Last sync activity timestamp (ms epoch) or null. */
  lastSyncAt: number | null;
  /** Whether the underlying MeshNode is started. */
  started: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// INVITE CODE FORMAT
// ─────────────────────────────────────────────────────────────────────────────
//
// Wire format:  mesh:v1:<base32 of JSON payload>
//
// Payload shape:
//   {
//     v: 1,
//     jti: <inviteId>,
//     iss: <issuer peerId>,
//     addrs: [<multiaddr>, ...],
//     exp: <ISO expiry or null>,
//     pk: <issuer pubkey hex>,
//     sig: <issuer signature, hex, over canonical(payload without sig)>,
//   }
//
// The signature is computed over the canonical JSON of the payload with
// the `sig` field omitted. Verifiers reconstruct the canonical form and
// check the signature against `pk`.

export const INVITE_CODE_PREFIX = 'mesh:v1:';

interface InvitePayload {
  v: 1;
  jti: string;
  iss: string;
  addrs: string[];
  exp: string | null;
  pk: string;
  sig?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPLEMENTATION
// ─────────────────────────────────────────────────────────────────────────────

export class MeshManager {
  constructor(private ctx: AppContext) {}

  // ───────────────────────────────────────────────────────────────────────
  // bootstrap
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Dial known peers from peer_book. With the MockMeshNode (no real libp2p),
   * this is mostly a no-op — it walks the peer_book, attempts a stubbed
   * dial via `libp2p().dial()` if available, and tracks results.
   *
   * Production runs against RealMeshNode where libp2p().dial() actually
   * establishes a connection.
   */
  async bootstrap(opts: BootstrapOptions = {}): Promise<BootstrapResult> {
    const ctx = this.ctx;
    const maxDials = opts.maxDials ?? 8;
    const excludeBlocked = opts.excludeBlocked !== false;

    if (!ctx.meshNode.started) {
      throw new Error('MeshManager.bootstrap: mesh node not started');
    }

    // Pick peers most-recently-seen first.
    let candidates = await ctx.storage.db.peer_book
      .orderBy('lastSeenAt')
      .reverse()
      .toArray();
    if (excludeBlocked) {
      candidates = candidates.filter((p) => p.trustLevel !== 'blocked');
    }
    candidates = candidates.slice(0, maxDials);

    const result: BootstrapResult = {
      attempted: 0,
      succeeded: 0,
      failed: 0,
      failures: [],
    };

    const libp2p = ctx.meshNode.libp2p() as { dial?: (addr: string) => Promise<unknown> } | null;

    for (const peer of candidates) {
      for (const addr of peer.multiaddrs) {
        result.attempted += 1;
        if (libp2p === null || typeof libp2p.dial !== 'function') {
          // MockMeshNode case — skip the actual dial but count as failure
          // (since no connection was actually made).
          result.failed += 1;
          result.failures.push({
            peerId: peer.peerId,
            error: 'no libp2p handle (mock node)',
          });
          continue;
        }
        try {
          await libp2p.dial(addr);
          result.succeeded += 1;
          // Update lastSeenAt for successful dial.
          await ctx.storage.db.peer_book.update(peer.peerId, {
            lastSeenAt: new Date().toISOString(),
          });
          break;  // one successful multiaddr per peer is enough
        } catch (err) {
          result.failed += 1;
          result.failures.push({
            peerId: peer.peerId,
            error: errMsg(err),
          });
        }
      }
    }

    await this.audit({
      category: 'mesh',
      action: 'mesh.bootstrap',
      context: null,
      details: {
        attempted: result.attempted,
        succeeded: result.succeeded,
        failed: result.failed,
        maxDials,
      },
    });

    return result;
  }

  // ───────────────────────────────────────────────────────────────────────
  // generateInviteCode
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Generate a peer-level invite code. Signs the payload with our own
   * identity under a stable mesh context so redeemers can verify
   * authenticity.
   *
   * The resulting code is base32-encoded and prefixed with `mesh:v1:` —
   * suitable for QR codes and verbal sharing alike.
   */
  async generateInviteCode(opts: GenerateInviteOptions = {}): Promise<GenerateInviteResult> {
    const ctx = this.ctx;
    const context = opts.context ?? defaultMeshContext();
    const inviteId = randomHex(16);
    const issuedAt = new Date().toISOString();
    const expiresAt =
      opts.expiresAt === undefined
        ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        : opts.expiresAt;

    // Collect our multiaddrs from the running mesh node. For MockMeshNode
    // we have none; we record an empty list. For real nodes this is real.
    const multiaddrs = await this.ownMultiaddrs();

    // Build payload (without sig).
    const issuerPubkey = await ctx.identity.pubkey(context);
    const pubkeyHex = bytesToHex(issuerPubkey);

    const payloadNoSig: Omit<InvitePayload, 'sig'> = {
      v: 1,
      jti: inviteId,
      iss: ctx.meshNode.peerId,
      addrs: multiaddrs,
      exp: expiresAt,
      pk: pubkeyHex,
    };

    // Sign the canonical form.
    const canonical = canonicalJSON(payloadNoSig);
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      context,
    );
    const sigHex = bytesToHex(signature);

    const payload: InvitePayload = { ...payloadNoSig, sig: sigHex };
    const code = INVITE_CODE_PREFIX + encodeBase32(new TextEncoder().encode(JSON.stringify(payload)));

    // Record in ledger.
    const record: InviteLedgerRecord = {
      inviteId,
      code,
      ...(opts.issuedFor !== undefined && { issuedFor: opts.issuedFor }),
      context: contextToKey(context),
      issuedAt,
      expiresAt,
      maxUses: opts.maxUses ?? 1,
      uses: 0,
    };
    await ctx.storage.db.invite_ledger.put(record);

    await this.audit({
      category: 'mesh',
      action: 'mesh.invite-generated',
      context: contextToKey(context),
      details: {
        inviteId,
        expiresAt,
        maxUses: record.maxUses,
        ...(opts.issuedFor !== undefined && { issuedFor: opts.issuedFor }),
      },
    });

    return { inviteId, code, expiresAt };
  }

  // ───────────────────────────────────────────────────────────────────────
  // redeemInviteCode
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Process an incoming peer invite code. Validates signature + expiry,
   * adds the issuer to peer_book, and (in production) triggers a dial.
   *
   * If the code is malformed, expired, or has an invalid signature, throws.
   * If the issuer is already in peer_book, merges the multiaddrs in.
   */
  async redeemInviteCode(opts: RedeemInviteOptions): Promise<RedeemInviteResult> {
    const ctx = this.ctx;
    const payload = decodeInviteCode(opts.code);

    // Expiry check.
    if (payload.exp !== null && new Date(payload.exp).getTime() < Date.now()) {
      throw new Error('MeshManager.redeemInviteCode: invite has expired');
    }

    // Signature check. We verify by reconstructing canonical(payload without sig)
    // and validating against payload.pk. If signature is missing, we skip
    // verification (some legitimate codes may be unsigned in degraded modes).
    let signatureVerified = false;
    if (typeof payload.sig === 'string' && payload.sig.length > 0) {
      const { sig, ...rest } = payload;
      void sig;  // explicitly discard
      const canonical = canonicalJSON(rest);
      try {
        const ok = await ctx.identity.verify(
          new TextEncoder().encode(canonical),
          hexToBytes(payload.sig),
          defaultMeshContext(),
          hexToBytes(payload.pk),
        );
        signatureVerified = ok;
      } catch (err) {
        // verify() may throw if SignerIdentity doesn't expose it; treat as
        // unverified rather than fatal. The user can still proceed with
        // the invite, but at lower trust.
        // eslint-disable-next-line no-console
        console.warn(`MeshManager.redeemInviteCode: verify threw: ${errMsg(err)}`);
        signatureVerified = false;
      }
    }

    // Add or merge into peer_book.
    const now = new Date().toISOString();
    const existing = await ctx.storage.db.peer_book.get(payload.iss);
    if (existing === undefined) {
      const record: PeerBookRecord = {
        peerId: payload.iss,
        multiaddrs: payload.addrs,
        firstSeenAt: now,
        lastSeenAt: now,
        trustLevel: signatureVerified ? 'known' : 'unknown',
        source: 'invite',
      };
      await ctx.storage.db.peer_book.put(record);
    } else {
      // Merge multiaddrs (de-dup).
      const merged = Array.from(new Set([...existing.multiaddrs, ...payload.addrs]));
      await ctx.storage.db.peer_book.update(payload.iss, {
        multiaddrs: merged,
        lastSeenAt: now,
        // Upgrade trust if signature verified and we previously didn't know.
        ...(signatureVerified && existing.trustLevel === 'unknown' && { trustLevel: 'known' }),
      });
    }

    await this.audit({
      category: 'mesh',
      action: 'mesh.invite-redeemed',
      context: null,
      details: {
        inviteId: payload.jti,
        issuerPeerId: payload.iss,
        signatureVerified,
        addrCount: payload.addrs.length,
      },
    });

    return {
      issuerPeerId: payload.iss,
      multiaddrs: payload.addrs,
      signatureVerified,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // addPeerMultiaddr
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Manually add a peer + multiaddr to the peer_book. Useful for testing,
   * for adding well-known bootstrap nodes, or for importing peers from
   * out-of-band channels.
   *
   * Idempotent on peerId — re-adding the same peerId with a different
   * multiaddr appends to the existing row's multiaddrs.
   */
  async addPeerMultiaddr(opts: AddPeerMultiaddrOptions): Promise<void> {
    const ctx = this.ctx;
    const now = new Date().toISOString();
    const existing = await ctx.storage.db.peer_book.get(opts.peerId);
    if (existing === undefined) {
      const record: PeerBookRecord = {
        peerId: opts.peerId,
        multiaddrs: [opts.multiaddr],
        firstSeenAt: now,
        lastSeenAt: now,
        trustLevel: opts.trustLevel ?? 'unknown',
        source: opts.source ?? 'manual',
        ...(opts.alias !== undefined && { alias: opts.alias }),
      };
      await ctx.storage.db.peer_book.put(record);
    } else {
      const merged = Array.from(new Set([...existing.multiaddrs, opts.multiaddr]));
      const updates: Partial<PeerBookRecord> = { multiaddrs: merged };
      if (opts.alias !== undefined) updates.alias = opts.alias;
      if (opts.trustLevel !== undefined) updates.trustLevel = opts.trustLevel;
      await ctx.storage.db.peer_book.update(opts.peerId, updates);
    }

    await this.audit({
      category: 'mesh',
      action: 'mesh.peer-added',
      context: null,
      details: {
        peerId: opts.peerId,
        multiaddr: opts.multiaddr,
        source: opts.source ?? 'manual',
      },
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // listPeers
  // ───────────────────────────────────────────────────────────────────────

  async listPeers(opts: ListPeersOptions = {}): Promise<PeerBookRecord[]> {
    const db = this.ctx.storage.db;
    let rows = await db.peer_book.toArray();
    if (opts.trustLevel !== undefined) {
      rows = rows.filter((p) => p.trustLevel === opts.trustLevel);
    }
    if (opts.includeUnseen === false) {
      rows = rows.filter((p) => p.lastSeenAt !== p.firstSeenAt);
    }
    // Most-recently-seen first.
    rows.sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
    return rows;
  }

  // ───────────────────────────────────────────────────────────────────────
  // getMeshStats
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Stable per-device NeighborhoodId — first 16 hex chars of BLAKE3 over
   * the mesh-context pubkey. Each ALDS install has its own NeighborhoodId;
   * federation between neighborhoods (Phase 9) is recorded in the
   * federation_links table, NOT by sharing this id.
   */
  async neighborhoodId(): Promise<string> {
    const ctx = this.ctx;
    const pub = await ctx.identity.pubkey(defaultMeshContext());
    return bytesToHex(blake3(pub)).slice(0, 16);
  }

  async getMeshStats(): Promise<MeshStats> {
    const ctx = this.ctx;
    const node = await ctx.meshNode.stats();
    const peers = await ctx.storage.db.peer_book.toArray();
    const byTrust: Record<PeerTrustLevel, number> = {
      unknown: 0, known: 0, trusted: 0, blocked: 0,
    };
    for (const p of peers) {
      byTrust[p.trustLevel] += 1;
    }
    return {
      peerId: node.peerId,
      connected: node.connected,
      known: peers.length,
      byTrust,
      uptimeSeconds: Math.floor(node.uptime / 1000),
      lastSyncAt: node.lastSyncAt,
      started: ctx.meshNode.started,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // markPeerSeen
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Update lastSeenAt for a peer. Called by code paths that have just
   * successfully interacted with a peer. Idempotent if the peer doesn't
   * exist (no-op).
   */
  async markPeerSeen(peerId: string): Promise<void> {
    const row = await this.ctx.storage.db.peer_book.get(peerId);
    if (row === undefined) return;
    await this.ctx.storage.db.peer_book.update(peerId, {
      lastSeenAt: new Date().toISOString(),
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERNAL
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Collect our own listening multiaddrs from the running mesh node.
   * Returns an empty list under MockMeshNode (no real network identity).
   */
  private async ownMultiaddrs(): Promise<string[]> {
    const libp2p = this.ctx.meshNode.libp2p() as
      | { getMultiaddrs?: () => Array<{ toString(): string }> }
      | null;
    if (libp2p === null || typeof libp2p.getMultiaddrs !== 'function') {
      return [];
    }
    try {
      return libp2p.getMultiaddrs().map((m) => m.toString());
    } catch {
      return [];
    }
  }

  private async audit(input: {
    category: AuditLogRecord['category'];
    action: string;
    context: string | null;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.ctx;
    const occurredAt = new Date().toISOString();
    const canonical = canonicalJSON({
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      defaultMeshContext(),
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try {
      await ctx.storage.db.audit_log.add(record);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`MeshManager.audit: failed to write audit log: ${errMsg(err)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function defaultMeshContext(): Context {
  // Mesh-level operations use 'hub' environment since they're not scoped
  // to any specific surface (chat, voice, etc).
  return { environment: 'hub', purpose: 'mesh' };
}

function contextToKey(ctx: Context): string {
  return `${ctx.environment}:${ctx.purpose}${ctx.epoch !== undefined ? `:e${ctx.epoch}` : ''}`;
}

function randomHex(byteLen: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLen));
  return bytesToHex(bytes);
}

function bytesToHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += b[i]!.toString(16).padStart(2, '0');
  return s;
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('hexToBytes: odd length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function encodeBase32(bytes: Uint8Array): string {
  // @scure/base.base32 uses RFC 4648 alphabet without padding.
  return base32.encode(bytes);
}

function decodeBase32(s: string): Uint8Array {
  return base32.decode(s);
}

/** Canonical JSON: keys sorted recursively. Stable byte representation for
 * signing. */
function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map((v) => canonicalJSON(v)).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return (
    '{' +
    keys.map((k) => JSON.stringify(k) + ':' + canonicalJSON(obj[k])).join(',') +
    '}'
  );
}

function decodeInviteCode(code: string): InvitePayload {
  if (!code.startsWith(INVITE_CODE_PREFIX)) {
    throw new Error(`decodeInviteCode: expected prefix ${INVITE_CODE_PREFIX}`);
  }
  const b32 = code.slice(INVITE_CODE_PREFIX.length);
  let bytes: Uint8Array;
  try {
    bytes = decodeBase32(b32.toUpperCase());
  } catch (err) {
    throw new Error(`decodeInviteCode: invalid base32: ${errMsg(err)}`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch (err) {
    throw new Error(`decodeInviteCode: invalid JSON: ${errMsg(err)}`);
  }
  if (
    typeof parsed !== 'object' || parsed === null ||
    (parsed as { v?: unknown }).v !== 1
  ) {
    throw new Error('decodeInviteCode: unsupported version');
  }
  const p = parsed as Record<string, unknown>;
  if (
    typeof p.jti !== 'string' ||
    typeof p.iss !== 'string' ||
    !Array.isArray(p.addrs) ||
    typeof p.pk !== 'string' ||
    (p.exp !== null && typeof p.exp !== 'string')
  ) {
    throw new Error('decodeInviteCode: missing required fields');
  }
  return {
    v: 1,
    jti: p.jti,
    iss: p.iss,
    addrs: p.addrs as string[],
    exp: p.exp as string | null,
    pk: p.pk,
    ...(typeof p.sig === 'string' && { sig: p.sig }),
  };
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
